/**
 * @file Tippy Manager Service
 * @description Centralized tooltip management using Tippy.js v6
 * Handles initialization, dynamic binding, and prevents conflicts
 */

/**
 * Tippy Manager Class
 * Manages all tooltip instances and provides centralized control
 */
class TippyManager {
  constructor() {
    this.instances = new Map();
    this.configs = new Map();
    this.initialized = false;
    this.observer = null;
  }

  /**
   * Initialize Tippy Manager
   */
  init() {
    if (this.initialized || typeof tippy === "undefined") {
      return false;
    }

    console.debug("Tippy Manager: Initializing...");

    // Set up default configurations
    this.setupDefaultConfigs();

    // Initialize existing elements
    this.initExistingElements();

    // Set up mutation observer for dynamic content
    this.setupMutationObserver();

    this.initialized = true;
    console.debug("Tippy Manager: Initialized successfully");
    return true;
  }

  /**
   * Setup default tooltip configurations
   */
  setupDefaultConfigs() {
    // Default configuration for data-tippy-content elements
    this.configs.set("default", {
      theme: "light-border",
      allowHTML: true,
      delay: [200, 0], // Show after 200ms, hide immediately
      duration: [300, 200], // Show for 300ms, hide in 200ms
      placement: "top",
      interactive: false,
      appendTo: () => document.body,
    });

    // Configuration for logo tooltips
    this.configs.set("logo", {
      content: (reference) => {
        const imgSrc = reference.getAttribute("data-img");
        if (!imgSrc) return "No image available";
        return `<img src="${imgSrc}" class="aspect-square w-20 object-contain object-center" alt="Logo">`;
      },
      theme: "light-border",
      allowHTML: true,
      delay: [300, 0],
      duration: [400, 200],
      placement: "top",
      interactive: false,
      appendTo: () => document.body,
      onShow(instance) {
        // Validate image exists
        const img = instance.popper.querySelector("img");
        if (img) {
          img.onerror = () => {
            instance.setContent("Image not available");
          };
        }
      },
    });

    // Configuration for region tooltips (dynamic content) - ONLY for .mapsvg-region
    this.configs.set("region", {
      content: "Loading...",
      theme: "light-border",
      allowHTML: true,
      followCursor: true,
      delay: [100, 0],
      duration: [300, 100],
      placement: "top",
      interactive: true,
      appendTo: () => document.body,
    });

    // Configuration for FDI marker tooltips
    this.configs.set("fdi-marker", {
      content: (reference) => {
        // Find the inner element that contains the data attributes
        const innerElement = reference.querySelector(".fdi-icon-wrapper") || reference;

        const displayName = innerElement.getAttribute("data-display-name");
        const locationName = innerElement.getAttribute("data-location-name");
        const locationId = innerElement.getAttribute("data-location-id");

        // Priority: display_name > location_name > fallback
        if (displayName && displayName !== "null" && displayName.trim() !== "") {
          return displayName;
        }
        if (locationName && locationName !== "null" && locationName.trim() !== "") {
          return locationName;
        }
        if (locationId && locationId !== "null" && locationId.trim() !== "") {
          return `Location ${locationId}`;
        }
        return "Unknown Location";
      },
      theme: "light-border",
      allowHTML: true,
      delay: [300, 0], // Show after 300ms, hide immediately
      duration: [400, 200], // Show for 400ms, hide in 200ms
      placement: "top",
      interactive: false,
      appendTo: () => document.body,
      onShow(instance) {
        // Ensure tooltip content is properly set
        const reference = instance.reference;
        const content = instance.props.content(reference);
        if (content && content !== instance.state.content) {
          instance.setContent(content);
        }
      },
    });
  }

  /**
   * Initialize existing elements on page load
   */
  initExistingElements() {
    // Initialize default tooltips
    this.initSelector("[data-tippy-content]", "default");

    // Initialize logo tooltips
    this.initSelector(".tippy-logo", "logo");

    // Initialize region tooltips ONLY for .mapsvg-region (not .mps-regions)
    this.initSelector(".mapsvg-region", "region");

    // DON'T auto-initialize FDI marker tooltips - let MarkerManager handle it
    // This prevents conflicts between auto-initialization and manual initialization
    // this.initSelector('.fdi-marker', 'fdi-marker');
  }

  /**
   * Initialize tooltips for a specific selector
   */
  initSelector(selector, configKey = "default") {
    const elements = document.querySelectorAll(selector);
    const config = this.configs.get(configKey);

    if (!config) {
      console.warn(`Tippy Manager: Configuration '${configKey}' not found`);
      return;
    }

    elements.forEach((element) => {
      this.createInstance(element, config, configKey);
    });

    console.debug(
      `Tippy Manager: Initialized ${elements.length} tooltips for selector '${selector}'`,
    );
  }

  /**
   * Create a new Tippy instance
   */
  createInstance(element, config, configKey) {
    // Check if element already has a Tippy instance
    if (element._tippy) {
      console.debug("Tippy Manager: Element already has Tippy instance, skipping");
      return element._tippy;
    }

    try {
      const instance = tippy(element, config);
      this.instances.set(element, { instance, configKey });

      // Store reference for cleanup
      element._tippyManager = this;

      return instance;
    } catch (error) {
      console.error("Tippy Manager: Failed to create instance for element:", element, error);
      return null;
    }
  }

  /**
   * Setup mutation observer for dynamic content
   */
  setupMutationObserver() {
    if (!window.MutationObserver) {
      console.warn("Tippy Manager: MutationObserver not supported");
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.handleNewElement(node);
            }
          });
        }
      });
    });

    // Observe the entire document for new elements
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    console.debug("Tippy Manager: Mutation observer setup complete");
  }

  /**
   * Handle new elements added to DOM
   */
  handleNewElement(element) {
    // Check for data-tippy-content elements
    if (element.hasAttribute("data-tippy-content")) {
      this.createInstance(element, this.configs.get("default"), "default");
    }

    // Check for tippy-logo elements
    if (element.classList.contains("tippy-logo")) {
      this.createInstance(element, this.configs.get("logo"), "logo");
    }

    // Check for .mapsvg-region elements (NOT .mps-regions)
    if (element.classList.contains("mapsvg-region")) {
      this.createInstance(element, this.configs.get("region"), "region");
    }

    // Check for FDI marker elements - DISABLED to prevent conflicts
    // FDI markers are handled by MarkerManager instead
    // if (element.classList.contains('fdi-marker')) {
    //   this.createInstance(element, this.configs.get('fdi-marker'), 'fdi-marker');
    // }

    // Check child elements
    element.querySelectorAll("[data-tippy-content]").forEach((el) => {
      if (!el._tippy) {
        this.createInstance(el, this.configs.get("default"), "default");
      }
    });

    element.querySelectorAll(".tippy-logo").forEach((el) => {
      if (!el._tippy) {
        this.createInstance(el, this.configs.get("logo"), "logo");
      }
    });

    // Only initialize .mapsvg-region, NOT .mps-regions
    element.querySelectorAll(".mapsvg-region").forEach((el) => {
      if (!el._tippy) {
        this.createInstance(el, this.configs.get("region"), "region");
      }
    });

    // Initialize FDI marker tooltips in child elements - DISABLED to prevent conflicts
    // FDI markers are handled by MarkerManager instead
    // element.querySelectorAll('.fdi-marker').forEach(el => {
    //   if (!el._tippy) {
    //     this.createInstance(el, this.configs.get('fdi-marker'), 'fdi-marker');
    //   }
    // });
  }

  /**
   * Update tooltip content dynamically
   */
  updateContent(selector, content) {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => {
      const instance = element._tippy;
      if (instance) {
        instance.setContent(content);
      }
    });
  }

  /**
   * Destroy all tooltip instances
   */
  destroy() {
    this.instances.forEach(({ instance }) => {
      if (instance && instance.destroy) {
        instance.destroy();
      }
    });

    this.instances.clear();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.initialized = false;
    console.debug("Tippy Manager: Destroyed all instances");
  }

  /**
   * Get statistics about tooltip instances
   */
  getStats() {
    const stats = {
      total: this.instances.size,
      byConfig: {},
      initialized: this.initialized,
    };

    this.instances.forEach(({ configKey }) => {
      stats.byConfig[configKey] = (stats.byConfig[configKey] || 0) + 1;
    });

    return stats;
  }
}

// Create singleton instance
const tippyManager = new TippyManager();

/**
 * Initialize Tippy Manager
 */
export function initTippyManager() {
  return tippyManager.init();
}

/**
 * Initialize tooltips for specific selector
 */
export function initTippySelector(selector, configKey = "default") {
  return tippyManager.initSelector(selector, configKey);
}

/**
 * Update tooltip content
 */
export function updateTippyContent(selector, content) {
  return tippyManager.updateContent(selector, content);
}

/**
 * Get Tippy Manager instance
 */
export function getTippyManager() {
  return tippyManager;
}

/**
 * Legacy compatibility function
 */
export function initTippyLegacy() {
  console.warn("Tippy Manager: Using legacy initialization - consider using initTippyManager()");
  return initTippyManager();
}
